import type { IntentClassificationData } from '../../query-intent-api/types';
import type { NLPSearchResultContainerDataTypeContainerType } from '../nlp-search-result-container-data-type-container-type';
import type { NLPSearchType } from '../types';

export type BaseUserJson = {
	user_base_data: {
		ari: string;
		account_id: string;
		name: string;
		email: string | null;
		avatar_url: string | null;
		profile_url: string | null;
		extended_profile: {
			job_title: string | null;
			location: string | null;
			department: string | null;
			organization: string | null;
		} | null;
	};
	time: string | null;
};

export type AdditionalMatch = {
	name: string;
	avatar_url: string;
	profile_url: string;
	title: string;
	email?: string;
};

export type RecentActivityType = {
	title: string;
	url: string;
	containerName: string;
	event: string;
	timestamp: string;
	iconUrl: string;
	contributors: {
		name: string;
		accountId: string;
	}[];
	othersCount: number;
};

export type TeamType = {
	id: string;
	displayName: string;
	description: string;
	memberCount: number;
	smallAvatarImageUrl: string;
};

export type ActivityOutputJson = {
	enriched_activity_output: {
		main_user_profile: BaseUserJson & {
			view_all_activity_url: string;
			recent_activity: RecentActivityType[];
		};
		additional_matches: Array<AdditionalMatch>;
	};
};

export type ProfileOutputJson = {
	enriched_profile_output: {
		main_user_profile: BaseUserJson & {
			collaborators: Array<BaseUserJson['user_base_data']>;
			collaboration_spaces: Array<{
				name: string;
				id: string;
				container_type: NLPSearchResultContainerDataTypeContainerType;
				url: string;
				icon_url: string;
			}>;
			view_all_activity_url?: string;
			recent_activity?: RecentActivityType[];
			teams?: TeamType[];
		};
		additional_matches: Array<AdditionalMatch>;
	};
};

export type SkipAIAnswerResult = { skip: true; data: NLPSearchType } | { skip: false };

export type IntentSkipHandler = (intentData: IntentClassificationData) => SkipAIAnswerResult;

export type StreamFailedErrorOptions = {
	details?: string;
	errorCode?: number;
	reason?: string;
	backendFailureType?: string;
	/** Trace id from the backend response, for correlation with backend logs. */
	traceId?: string;
};

export class StreamFailedError extends Error {
	details?: string;
	errorCode?: number;
	reason?: string;
	backendFailureType?: string;
	traceId?: string;

	constructor(message: string, opts?: StreamFailedErrorOptions) {
		super(message);
		this.name = 'StreamFailedError';
		this.details = opts?.details;
		this.errorCode = opts?.errorCode;
		this.reason = opts?.reason;
		this.backendFailureType = opts?.backendFailureType;
		this.traceId = opts?.traceId;
	}
}
